import * as React from 'react'

import { ChevronLeftIcon, ChevronRightIcon, FileText } from 'lucide-react'
import { inject, observer } from 'mobx-react'

import {
  IAttachment,
  IPermitTypeField,
  MediaViewType,
} from '~/client/graph/types.generated'
import { Button } from '~/client/src/shared/components/Button/Button'
import { getFileKind } from '~/client/src/shared/components/FilesInput/fileKind'
import PdfDocument from '~/client/src/shared/components/PdfDocument/PdfDocument'
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from '~/client/src/shared/components/shadcn-ui/carousel'
import IFilePreviewProperties from '~/client/src/shared/interfaces/IFilePreviewProperties'
import { SHOW_FULLSCREEN_PREVIEW } from '~/client/src/shared/stores/EventStore/eventConstants'
import EventsStore from '~/client/src/shared/stores/EventStore/Events.store'
import { openAttachmentPreview } from '~/client/src/shared/utils/filePreviewUtils'

interface IProps {
  typeField: IPermitTypeField
  eventsStore?: EventsStore
}

@inject('eventsStore')
@observer
export default class PermitFormInlineMediaField extends React.Component<IProps> {
  public render() {
    const { typeField, eventsStore } = this.props
    const items = (typeField.inlineMediaItems as IAttachment[]) || []
    const mediaView = typeField.mediaView || MediaViewType.Separated

    if (!items.length) {
      return null
    }

    return (
      <div className="tw:px-3 tw:py-2">
        <div className="tw:text-sm tw:font-medium tw:text-muted-foreground tw:mb-2">
          {typeField.caption} ({items.length})
        </div>
        {mediaView === MediaViewType.Separated ? (
          <SeparatedView items={items} eventsStore={eventsStore} />
        ) : (
          <GallerySliderView items={items} eventsStore={eventsStore} />
        )}
      </div>
    )
  }
}

function showPreview(
  eventsStore: EventsStore,
  files: IFilePreviewProperties[],
  index: number,
) {
  eventsStore.dispatch(SHOW_FULLSCREEN_PREVIEW, files, index)
}

function SeparatedView({
  items,
  eventsStore,
}: {
  items: IAttachment[]
  eventsStore: EventsStore
}) {
  return (
    <div className="tw:flex tw:flex-col tw:gap-2">
      {items.map((item, index) => (
        <div
          key={item.url}
          className="tw:cursor-pointer"
          onClick={() =>
            openAttachmentPreview(items, index, (f, i) =>
              showPreview(eventsStore, f, i),
            )
          }
        >
          <MediaItem item={item} />
        </div>
      ))}
    </div>
  )
}

function GallerySliderView({
  items,
  eventsStore,
}: {
  items: IAttachment[]
  eventsStore: EventsStore
}) {
  const [api, setApi] = React.useState<CarouselApi>(null)
  const [thumbApi, setThumbApi] = React.useState<CarouselApi>(null)
  const [current, setCurrent] = React.useState(0)

  React.useEffect(() => {
    if (!api) return

    setCurrent(api.selectedScrollSnap())

    const onSelect = () => {
      const index = api.selectedScrollSnap()
      setCurrent(index)
      thumbApi?.scrollTo(index)
    }
    api.on('select', onSelect)
    return () => {
      api.off('select', onSelect)
    }
  }, [api, thumbApi])

  const currentItem = items[current]

  return (
    <div className="tw:flex tw:flex-col tw:gap-2">
      <Carousel setApi={setApi} opts={{ loop: false }}>
        <CarouselContent>
          {items.map((item, index) => (
            <CarouselItem key={item.url}>
              <div
                className="tw:cursor-pointer tw:rounded-md tw:overflow-hidden tw:bg-muted"
                onClick={() =>
                  openAttachmentPreview(items, index, (f, i) =>
                    showPreview(eventsStore, f, i),
                  )
                }
              >
                <GalleryPreview item={item} />
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>

      <div className="tw:text-xs tw:text-muted-foreground tw:truncate">
        {currentItem?.fileName}
      </div>

      <Carousel setApi={setThumbApi} opts={{ align: 'start', dragFree: true }}>
        <CarouselContent className="tw:-ml-1.5">
          {items.map((item, index) => (
            <CarouselItem key={item.url} className="tw:basis-auto tw:pl-1.5">
              <button
                className={`tw:w-16 tw:h-12 tw:rounded tw:overflow-hidden tw:border-2 tw:cursor-pointer tw:p-0 tw:bg-transparent ${
                  index === current
                    ? 'tw:border-primary'
                    : 'tw:border-transparent tw:opacity-70 tw:hover:opacity-100'
                }`}
                onClick={() => api?.scrollTo(index)}
              >
                <ThumbnailContent item={item} />
              </button>
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>

      <div className="tw:flex tw:items-center tw:justify-center tw:gap-2">
        <Button
          variant="ghost"
          size="icon"
          className="tw:size-6"
          disabled={current === 0}
          onClick={() => api?.scrollTo(current - 1)}
        >
          <ChevronLeftIcon />
        </Button>
        <span className="tw:text-xs tw:text-muted-foreground">
          {current + 1} of {items.length}
        </span>
        <Button
          variant="ghost"
          size="icon"
          className="tw:size-6"
          disabled={current === items.length - 1}
          onClick={() => api?.scrollTo(current + 1)}
        >
          <ChevronRightIcon />
        </Button>
      </div>
    </div>
  )
}

function GalleryPreview({ item }: { item: IAttachment }): JSX.Element {
  const kind = getFileKind(item.fileName)

  if (kind === 'image') {
    return (
      <img
        src={item.url}
        alt={item.fileName}
        className="tw:w-full tw:max-h-60 tw:object-contain"
      />
    )
  }

  if (kind === 'video') {
    return (
      <video
        src={item.url}
        className="tw:w-full tw:max-h-60 tw:object-contain"
        controls
        preload="metadata"
      />
    )
  }

  if (kind === 'pdf') {
    return (
      <div className="tw:h-60 tw:overflow-hidden">
        <PdfDocument file={item.url} pageNumber={1} shouldFitToWidth={true} />
      </div>
    )
  }

  return (
    <div className="tw:h-40 tw:flex tw:items-center tw:justify-center">
      <FileText size={32} className="tw:text-muted-foreground" />
    </div>
  )
}

function ThumbnailContent({ item }: { item: IAttachment }): JSX.Element {
  const kind = getFileKind(item.fileName)

  if (kind === 'image') {
    return (
      <img
        src={item.url}
        alt={item.fileName}
        className="tw:w-full tw:h-full tw:object-cover"
      />
    )
  }

  if (kind === 'video') {
    return (
      <video
        src={item.url}
        className="tw:w-full tw:h-full tw:object-cover tw:bg-muted"
        muted
        preload="metadata"
      />
    )
  }

  return (
    <div className="tw:w-full tw:h-full tw:bg-muted tw:flex tw:items-center tw:justify-center">
      <FileText size={12} className="tw:text-muted-foreground" />
    </div>
  )
}

function MediaItem({ item }: { item: IAttachment }): JSX.Element {
  const kind = getFileKind(item.fileName)

  if (kind === 'image') {
    return (
      <img
        src={item.url}
        alt={item.fileName}
        className="tw:w-full tw:rounded-md tw:object-contain tw:max-h-60 tw:bg-muted"
      />
    )
  }

  if (kind === 'video') {
    return (
      <video
        src={item.url}
        className="tw:w-full tw:max-h-60 tw:rounded-md tw:bg-muted"
        controls
        preload="metadata"
      />
    )
  }

  if (kind === 'pdf') {
    return (
      <div className="tw:rounded-md tw:border tw:border-border tw:overflow-hidden">
        <div className="tw:h-60 tw:overflow-hidden">
          <PdfDocument file={item.url} pageNumber={1} shouldFitToWidth={true} />
        </div>
        <div className="tw:flex tw:items-center tw:gap-2 tw:px-3 tw:py-2 tw:border-t tw:border-border tw:bg-muted/30">
          <FileText
            size={14}
            className="tw:text-muted-foreground tw:shrink-0"
          />
          <span className="tw:text-xs tw:text-muted-foreground tw:truncate">
            {item.fileName}
          </span>
        </div>
      </div>
    )
  }

  return (
    <div className="tw:flex tw:items-center tw:gap-3 tw:rounded-md tw:border tw:border-border tw:p-3">
      <FileText size={20} className="tw:text-muted-foreground tw:shrink-0" />
      <span className="tw:text-sm tw:truncate">{item.fileName}</span>
    </div>
  )
}
